import { useEffect, useMemo, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import Pagination from '@mui/material/Pagination'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import type { ExceptionItem, Severity } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { EmptyState } from './EmptyState'
import {
  EXCEPTIONS_PAGE_SIZES,
  buildExceptionsExportPath,
  useExceptions,
  type ExceptionsFilter,
  type ExceptionsPageSize,
} from '../hooks/useApi'
import { tokens } from '../theme'

export function ExceptionBoard({
  filter,
  selectedId,
  onSelect,
}: {
  filter: ExceptionsFilter
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const [pageSize, setPageSize] = useState<ExceptionsPageSize>(50)
  const [page, setPage] = useState(1)

  // Reset back to page 1 any time the filter changes — otherwise an empty
  // page would show after narrowing results.
  const filterKey = JSON.stringify({
    severity: filter.severity ?? [],
    code: filter.code ?? [],
    subject_type: filter.subject_type ?? '',
  })
  useEffect(() => {
    setPage(1)
  }, [filterKey, pageSize])

  const { data, isLoading, isError, error, isFetching } = useExceptions(filter, page, pageSize)

  const items = useMemo<ExceptionItem[]>(() => data?.data ?? [], [data])
  const total = data?.meta?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <ExceptionToolbar
        filter={filter}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageSizeChange={(s) => setPageSize(s)}
      />

      {isLoading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Alert severity="error" variant="outlined" sx={{ m: 2 }}>
          Failed to load exceptions: {error instanceof Error ? error.message : 'unknown error'}
        </Alert>
      ) : items.length === 0 ? (
        <EmptyState
          title="No exceptions"
          hint="Quiet day — either the queue is genuinely clear or the filters are narrowing everything out."
          size="md"
        />
      ) : (
        <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
          {items.map((ex) => (
            <ExceptionRow
              key={ex.id}
              ex={ex}
              selected={ex.id === selectedId}
              onClick={() => onSelect(ex.id)}
            />
          ))}
        </Stack>
      )}

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.default',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {total === 0
            ? 'No records'
            : `Showing ${start}-${end} of ${total}${isFetching ? ' (refreshing…)' : ''}`}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {pageCount > 1 && (
          <Pagination
            size="small"
            page={page}
            count={pageCount}
            onChange={(_, p) => setPage(p)}
            siblingCount={1}
            boundaryCount={1}
            shape="rounded"
          />
        )}
      </Box>
    </Box>
  )
}

function ExceptionToolbar({
  filter,
  page,
  pageSize,
  total,
  onPageSizeChange,
}: {
  filter: ExceptionsFilter
  page: number
  pageSize: ExceptionsPageSize
  total: number
  onPageSizeChange: (s: ExceptionsPageSize) => void
}) {
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null)
  const { accessToken } = useAuth()

  const triggerDownload = (scope: 'page' | 'all') => {
    setExportAnchor(null)
    const path = buildExceptionsExportPath(filter, scope, page, pageSize)
    // CSV endpoint is Bearer-protected; fetch then blob so we can attach
    // the auth header. Direct <a href> would hit 401.
    void fetch(path, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Export failed: ${res.status}`)
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const cd = res.headers.get('Content-Disposition') ?? ''
        const match = /filename="([^"]+)"/.exec(cd)
        a.download = match?.[1] ?? 'fieldlens-exceptions.csv'
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      })
      .catch(() => {
        // Error surfaces in the caller's UI already via the main query;
        // a silent failure here is acceptable — a future toast stack will
        // surface it properly.
      })
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Rows per page
      </Typography>
      <Select
        size="small"
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value) as ExceptionsPageSize)}
        sx={{ minWidth: 80 }}
      >
        {EXCEPTIONS_PAGE_SIZES.map((n) => (
          <MenuItem key={n} value={n}>
            {n}
          </MenuItem>
        ))}
      </Select>
      <Box sx={{ flexGrow: 1 }} />
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setExportAnchor(e.currentTarget)}
        disabled={total === 0}
        sx={{ textTransform: 'none' }}
      >
        Export to Excel
      </Button>
      <Menu
        anchorEl={exportAnchor}
        open={!!exportAnchor}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem onClick={() => triggerDownload('page')}>Current page</MenuItem>
        <MenuItem onClick={() => triggerDownload('all')}>All filtered records (up to 10,000)</MenuItem>
      </Menu>
    </Box>
  )
}

function ExceptionRow({
  ex,
  selected,
  onClick,
}: {
  ex: ExceptionItem
  selected: boolean
  onClick: () => void
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        p: 1.5,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        borderLeft: selected ? `3px solid ${tokens.gold}` : '3px solid transparent',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: `2px solid ${tokens.gold}`, outlineOffset: -2 },
      }}
    >
      <SeverityPip severity={ex.severity} />
      <Box sx={{ minWidth: 140 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {ex.code}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {ex.category ?? ex.subject_type}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
          {ex.subject_summary || ex.subject_type}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {ex.suggested_action ?? '—'}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatAge(ex.opened_at)}
        </Typography>
        <Box>
          {ex.acknowledged_at && (
            <Chip label="ack" size="small" sx={{ bgcolor: tokens.goldLight, color: tokens.charcoal }} />
          )}
        </Box>
      </Box>
    </Box>
  )
}

function SeverityPip({ severity }: { severity: Severity }) {
  const colour =
    severity === 'critical' ? tokens.alert :
    severity === 'high' ? '#E09935' :
    severity === 'warn' ? tokens.gold :
    tokens.textSec
  return (
    <Box
      sx={{
        width: 8,
        height: 36,
        borderRadius: 2,
        bgcolor: colour,
        flexShrink: 0,
      }}
      aria-label={`severity ${severity}`}
    />
  )
}

function formatAge(iso: string): string {
  const then = new Date(iso).getTime()
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.round(hrs / 24)}d`
}
